import React from 'react';
import {useDispatch} from 'react-redux';
import {AppConfigService, parsePublicConfig} from '../api';
import {setPublicConfig} from '../store';
import useApiClient from './useApiClient';

type RefreshOptions = {debounce?: number};

/**
 * Refresh the public app configuration from `GET /api/app/flags` (#379): the
 * public flags, their rollouts and the per-platform minimum and recommended
 * versions, signed in or out. Returns the refresh function; each app calls it
 * on start and when it comes back to the foreground.
 *
 * A good answer replaces what is stored (a 200 is authoritative). A failed or
 * malformed one keeps it: offline must not flip a kill switch or drop a
 * minimum. Silent: a failure (offline, 5xx, an older backend) is not the
 * user's business, so it never raises the error snackbar.
 */
const usePublicConfigRefresh = () => {
  const dispatch = useDispatch();
  const {fetch} = useApiClient();

  return React.useCallback(
    (options?: RefreshOptions) => {
      fetch(AppConfigService.getPublicFlags(), {...options, silent: true})
        .then((data) => {
          const config = parsePublicConfig(data);
          if (config) {
            dispatch(setPublicConfig(config));
          }
        })
        .catch(() => {});
    },
    [fetch, dispatch],
  );
};

export default usePublicConfigRefresh;
